import { AppToggleSettings } from '../types';

// Default values for the app_setting_layout.xml toggles. Android's AppSettings
// bean leaves every boolean `false`, but in practice the app ships a server
// config with the common menus/features enabled. To keep a fresh install
// usable (and so toggling a switch OFF visibly *removes* a feature), the
// menus + features the app shows out of the box default to `true` here; the
// rest default to `false`. String keys hold radio / spinner selections.
export const APP_SETTING_DEFAULTS: AppToggleSettings = {
  // Theme Colour Setting — preset id from services/theme.ts
  theme_color: 'teal',

  // Menu Option — drawer/menu visibility (shown by default)
  enable_payment_menu: true,
  enable_goods_received_menu: true,
  enable_stock_out_menu: true,
  barcode_menu: true,
  sales_return: true,

  // Billing Screen Option (shown by default)
  wholesale: false, // Retail & Wholesale selector — hidden by default
  dinein_parcel: false, // restaurant feature — hidden by default
  show_hold_list_button: true,
  discount_amount_ui_option: true,
  enable_udn: false, // UDN box on the billing screen — hidden by default
  show_bill_details_section: false, // bill-details panel (payment mode, customer, totals) — hidden by default
  hide_product_search_box: false, // product search box on the billing screen shown by default
  allow_decimal_qty: false, // quantities are whole numbers unless this is enabled

  // Customer Setting (fields shown by default)
  customer_details: true,
  enable_customer_advance: true,
  enable_vehicle_number: true,

  // Product Setting
  product_list_style_enable: true, // the style radio below applies
  product_list_style: 'Normal', // Standard | Normal | Small
  variant: true, // variant chooser opens for products with variants

  // Category Setting
  category_grid_count: '8', // 8 | 10 | 12

  // Table / Captain (restaurant module — hidden by default; enable from
  // App Setting → Table / Captain sections for restaurant outlets)
  enable_tables: false,
  captain_app: false,
  ask_captain_details: false,
  table_list_style_choice: 'Standard',
  checkin_checkout: false, // Rooms (check-in/out) menu hidden by default
  table_extra_actions: true, // Table Actions menu (swap/merge/split/bill merge…) on the order panel
  table_action_swap: true,
  table_action_transfer: true,
  table_action_merge: true,
  table_action_move_items: true,
  table_action_bill_merge: true,
  table_action_split_bill: true,
  table_action_equal_split: true,
  table_action_print_bill: true,
  table_action_void: true,
  // Captain App gets the same menu under its own switches. Table moves and the
  // pre-bill are on by default; actions that take payment stay off because
  // settlement normally happens at the till.
  captain_table_actions: true,
  captain_action_swap: true,
  captain_action_transfer: true,
  captain_action_merge: true,
  captain_action_move_items: true,
  captain_action_bill_merge: false,
  captain_action_split_bill: false,
  captain_action_equal_split: false,
  captain_action_print_bill: true,
  captain_action_void: false,

  // Online (Swiggy & Zomato + Online Orders menus hidden by default)
  online_order_system: false,

  // Day Setting
  day_end_time: '12 AM',
  enable_shift: true, // Shifts / Shift End / Opening Balance menus

  // Service point / KOT printing (restaurant feature — off by default)
  multi_service_point_wise_product_print: false, // split KOTs per station printer
  kot_show_product_total_qty: false,             // TOTAL QTY line on KOTs

  // Tax / charges Setting
  isRoundOffAmt: true,   // round the final total to the nearest rupee
  isIGST: false,         // CGST + SGST split by default (intra-state)
  estimateMode: false,   // estimate / proforma bills off by default
  nonGST: false,         // GST outlet by default
  service_charge: false, // restaurant service-charge field off by default
  deliveryNote: false,   // delivery challan / vehicle fields off by default
};

// Resolve a setting, falling back to the documented default.
export function settingValue(
  settings: AppToggleSettings,
  key: string
): boolean | string {
  if (key in settings) return settings[key];
  if (key in APP_SETTING_DEFAULTS) return APP_SETTING_DEFAULTS[key];
  return false;
}

export function isOn(settings: AppToggleSettings, key: string): boolean {
  return settingValue(settings, key) === true;
}

export function choiceOf(settings: AppToggleSettings, key: string): string {
  const v = settingValue(settings, key);
  return typeof v === 'string' ? v : '';
}
